import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/gameConfig';
import { callGameStart, callGameGetData } from '../utils/gamebox';
import { ENCYCLOPEDIA_DATA_KEY, parseDiscoveredIds } from '../utils/encyclopedia';

export class TitleScene extends Phaser.Scene {
  private startButton!: Phaser.GameObjects.Container;
  private howToPlayButton!: Phaser.GameObjects.Container;
  private encyclopediaButton!: Phaser.GameObjects.Container;
  private howToPlayModal!: Phaser.GameObjects.Container;

  constructor() {
    super({ key: 'TitleScene' });
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const centerX = WIDTH / 2;

    // 図鑑データを取得（GMO API）
    callGameGetData((data) => {
      const ids = parseDiscoveredIds(data.gamedata || '{}');
      this.registry.set(ENCYCLOPEDIA_DATA_KEY, ids);
    });

    // 背景（バウハウス：白・幾何学図形）
    this.createBackground();

    // タイトルロゴ（元サイズ444x513の65％）
    const titleLogo = this.add.image(centerX, HEIGHT * 0.25, 'title_logo');
    titleLogo.setOrigin(0.5);
    titleLogo.setDisplaySize(289, 333);

    // サンプルアイテムの表示
    this.createSampleItems();

    // スタートボタン
    this.createStartButton(centerX, HEIGHT * 0.72);

    // あそびかたボタン
    this.createHowToPlayButton(centerX, HEIGHT * 0.82);

    // 図鑑ボタン
    this.createEncyclopediaButton(centerX, HEIGHT * 0.88);

    // タイトルアニメーション
    this.tweens.add({
      targets: titleLogo,
      y: titleLogo.y - 10,
      duration: 1500,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  private createBackground(): void {
    const { WIDTH, HEIGHT, COLORS } = GAME_CONFIG;

    // 背景（バウハウス：白）
    const graphics = this.add.graphics();
    graphics.fillStyle(COLORS.WHITE, 1);
    graphics.fillRect(0, 0, WIDTH, HEIGHT);

    // バウハウス的な幾何学図形（赤・黄・青の三原色）
    const shapes = [
      { x: WIDTH * 0.1, y: HEIGHT * 0.15, color: COLORS.PRIMARY, size: 80 },
      { x: WIDTH * 0.9, y: HEIGHT * 0.2, color: COLORS.YELLOW, size: 60 },
      { x: WIDTH * 0.85, y: HEIGHT * 0.85, color: COLORS.BLUE, size: 70 },
      { x: WIDTH * 0.15, y: HEIGHT * 0.8, color: COLORS.PRIMARY, size: 50 },
    ];

    shapes.forEach((s, i) => {
      if (i % 2 === 0) {
        this.add.rectangle(s.x, s.y, s.size, s.size, s.color, 0.15);
      } else {
        this.add.circle(s.x, s.y, s.size / 2, s.color, 0.15);
      }
    });
  }

  private createSampleItems(): void {
    const { WIDTH, HEIGHT, TOTAL_ITEM_TYPES } = GAME_CONFIG;
    const centerY = HEIGHT * 0.55;

    // ランダムなサンプルアイテムを表示
    const sampleCount = 5;
    const spacing = WIDTH / (sampleCount + 1);

    for (let i = 0; i < sampleCount; i++) {
      const itemIndex = Phaser.Math.Between(1, TOTAL_ITEM_TYPES);
      const item = this.add.image(
        spacing * (i + 1),
        centerY,
        `item_${itemIndex}`
      );
      item.setScale(Phaser.Math.FloatBetween(0.7, 0.8));

      // フワフワアニメーション
      this.tweens.add({
        targets: item,
        y: centerY + Phaser.Math.Between(-20, 20),
        angle: Phaser.Math.Between(-10, 10),
        duration: Phaser.Math.Between(1500, 2500),
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
        delay: i * 200,
      });
    }
  }

  private createStartButton(x: number, y: number): void {
    const { COLORS, FONTS } = GAME_CONFIG;

    // ボタン背景（バウハウス：角張った矩形、赤）
    const buttonBg = this.add.graphics();
    buttonBg.fillStyle(COLORS.PRIMARY, 1);
    buttonBg.fillRect(-150, -40, 300, 80);

    // ボタンテキスト
    const buttonText = this.add.text(0, 0, 'スタート', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.MEDIUM_SIZE}px`,
      color: '#ffffff',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5);

    // コンテナにまとめる
    this.startButton = this.add.container(x, y, [buttonBg, buttonText]);

    // インタラクティブ設定
    const hitArea = new Phaser.Geom.Rectangle(-150, -40, 300, 80);
    this.startButton.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    // ホバー効果
    this.startButton.on('pointerover', () => {
      this.tweens.add({
        targets: this.startButton,
        scale: 1.05,
        duration: 100,
      });
    });

    this.startButton.on('pointerout', () => {
      this.tweens.add({
        targets: this.startButton,
        scale: 1,
        duration: 100,
      });
    });

    // クリック時の処理
    this.startButton.on('pointerdown', () => {
      this.startButton.disableInteractive();
      this.sound.play('button');

      // ボタンのアニメーション
      this.tweens.add({
        targets: this.startButton,
        scale: 0.95,
        duration: 100,
        yoyo: true,
        onComplete: () => {
          // GMO API: ゲーム開始
          callGameStart(() => {
            this.scene.start('GameScene');
          });
        },
      });
    });

    // パルスアニメーション
    this.tweens.add({
      targets: this.startButton,
      scale: 1.03,
      duration: 800,
      ease: 'Sine.easeInOut',
      yoyo: true,
      repeat: -1,
    });
  }

  private createHowToPlayButton(x: number, y: number): void {
    const { COLORS, FONTS } = GAME_CONFIG;

    // ボタン背景（白、黒枠）
    const buttonBg = this.add.graphics();
    buttonBg.fillStyle(COLORS.WHITE, 1);
    buttonBg.fillRect(-100, -25, 200, 50);
    buttonBg.lineStyle(2, COLORS.BLACK, 1);
    buttonBg.strokeRect(-100, -25, 200, 50);

    // ボタンテキスト
    const buttonText = this.add.text(0, 0, 'あそびかた', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#000000',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5);

    // コンテナにまとめる
    this.howToPlayButton = this.add.container(x, y, [buttonBg, buttonText]);

    // インタラクティブ設定
    const hitArea = new Phaser.Geom.Rectangle(-100, -25, 200, 50);
    this.howToPlayButton.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    // ホバー効果
    this.howToPlayButton.on('pointerover', () => {
      buttonText.setColor('#e74c3c');
    });

    this.howToPlayButton.on('pointerout', () => {
      buttonText.setColor('#000000');
    });

    // クリック時の処理
    this.howToPlayButton.on('pointerdown', () => {
      this.sound.play('button');
      this.showHowToPlayModal();
    });
  }

  private createEncyclopediaButton(x: number, y: number): void {
    const { COLORS, FONTS } = GAME_CONFIG;

    // ボタン背景（白、黒枠）※「おっさん図鑑」用に幅を広め
    const buttonBg = this.add.graphics();
    buttonBg.fillStyle(COLORS.WHITE, 1);
    buttonBg.fillRect(-100, -22, 200, 44);
    buttonBg.lineStyle(2, COLORS.BLACK, 1);
    buttonBg.strokeRect(-100, -22, 200, 44);

    // ボタンテキスト
    const buttonText = this.add.text(0, 0, 'おっさん図鑑', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#000000',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5);

    this.encyclopediaButton = this.add.container(x, y, [buttonBg, buttonText]);

    const hitArea = new Phaser.Geom.Rectangle(-100, -22, 200, 44);
    this.encyclopediaButton.setInteractive(hitArea, Phaser.Geom.Rectangle.Contains);

    this.encyclopediaButton.on('pointerover', () => {
      buttonText.setColor('#e74c3c');
    });

    this.encyclopediaButton.on('pointerout', () => {
      buttonText.setColor('#000000');
    });

    this.encyclopediaButton.on('pointerdown', () => {
      this.sound.play('button');
      this.scene.start('EncyclopediaScene');
    });
  }

  private showHowToPlayModal(): void {
    const { WIDTH, HEIGHT, COLORS, FONTS } = GAME_CONFIG;

    // 背景オーバーレイ
    const overlay = this.add.rectangle(
      WIDTH / 2,
      HEIGHT / 2,
      WIDTH,
      HEIGHT,
      0x000000,
      0.7
    );
    overlay.setDepth(100);

    // モーダル背景（幅600。高さは後で調整）
    const modalW = 600;
    let modalH = 500;
    const modalBg = this.add.graphics();
    modalBg.fillStyle(COLORS.WHITE, 1);
    modalBg.fillRect(-modalW / 2, -modalH / 2, modalW, modalH);
    modalBg.lineStyle(4, COLORS.BLACK, 1);
    modalBg.strokeRect(-modalW / 2, -modalH / 2, modalW, modalH);

    // タイトル（上部余白を抑えて配置）
    const title = this.add.text(0, -modalH / 2 + 28, 'あそびかた', {
      fontFamily: FONTS.DEFAULT,
      fontSize: '40px',
      color: '#000000',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);

    // 説明文（タイトルと十分な間隔を空け、上揃えで配置）
    const instructionsY = -modalH / 2 + 88;
    const instructions = this.add.text(
      0,
      instructionsY,
      '① 画面上部に「探すおっさん」が\n   表示されます\n\n' +
        '② 画面内からおっさんを見つけて\n   タップしよう！\n\n' +
        '③ 正解で +100点、不正解で -20点\n\n' +
        '④ 全員見つけるとステージクリア！\n   タイムボーナスがもらえます\n\n' +
        '⑤ 制限時間内にハイスコアを目指そう！',
      {
        fontFamily: FONTS.DEFAULT,
        fontSize: '20px',
        color: '#333333',
        lineSpacing: 10,
      }
    );
    instructions.setOrigin(0.5, 0);

    // とじる（⑤の下に配置。説明文の実際の高さを使用）
    const instructionsBounds = instructions.getBounds();
    const closeBtnY = instructionsY + instructionsBounds.height + 40;
    const closeBtn = this.add.text(0, closeBtnY, 'とじる', {
      fontFamily: FONTS.DEFAULT,
      fontSize: `${FONTS.SMALL_SIZE}px`,
      color: '#e74c3c',
      fontStyle: 'bold',
    });
    closeBtn.setOrigin(0.5);
    closeBtn.setInteractive({ useHandCursor: true });

    // モーダル高さをとじるの位置に合わせて調整
    modalH = Math.max(500, (closeBtnY + 50) * 2);
    modalBg.clear();
    modalBg.fillStyle(COLORS.WHITE, 1);
    modalBg.fillRect(-modalW / 2, -modalH / 2, modalW, modalH);
    modalBg.lineStyle(4, COLORS.BLACK, 1);
    modalBg.strokeRect(-modalW / 2, -modalH / 2, modalW, modalH);

    // コンテナにまとめる
    this.howToPlayModal = this.add.container(WIDTH / 2, HEIGHT / 2, [
      modalBg,
      title,
      instructions,
      closeBtn,
    ]);
    this.howToPlayModal.setDepth(101);

    // 閉じる処理
    const closeModal = () => {
      this.sound.play('button');
      overlay.destroy();
      this.howToPlayModal.destroy();
    };

    closeBtn.on('pointerdown', closeModal);
    overlay.setInteractive();
    overlay.on('pointerdown', closeModal);
  }
}
